const CUSTOM_SCROLLBAR_MIN_THUMB_PX = 40;
const DRAG_CLICK_THRESHOLD_PX = 5;

const slideshowCustomScrollbar = () => {
  document.querySelectorAll('.slideshow-custom-scrollbar').forEach((scrollbar) => {
    const sliderComponent = scrollbar.closest('slider-component');
    const scroller = sliderComponent && sliderComponent.querySelector('.slider');
    const track = scrollbar.querySelector('.slideshow-custom-scrollbar__track');
    const thumb = scrollbar.querySelector('.slideshow-custom-scrollbar__thumb');

    // Re-run for sections rendered after load (YMAL); skip ones already wired up.
    if (!scroller || !track || !thumb || scrollbar.dataset.scrollbarInit) return;
    scrollbar.dataset.scrollbarInit = 'true';

    const updateThumb = () => {
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      scrollbar.hidden = false;

      if (maxScroll <= 0) {
        thumb.style.width = `${track.clientWidth}px`;
        thumb.style.transform = 'translateX(0)';
        return;
      }

      const trackWidth = track.clientWidth;
      const ratio = scroller.clientWidth / scroller.scrollWidth;
      const thumbWidth = Math.max(ratio * trackWidth, CUSTOM_SCROLLBAR_MIN_THUMB_PX);
      const trackRange = Math.max(trackWidth - thumbWidth, 1);
      const thumbOffset = (scroller.scrollLeft / maxScroll) * trackRange;

      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${thumbOffset}px)`;
    };

    // scrollLeft writes + the thumb update are batched into a single rAF callback,
    // once per paint, shared by both drag sources below (track-drag and slider-drag
    // can't both be active from one pointer at once). mousemove can fire far more
    // often than the screen repaints; doing the write + immediate readback of
    // scrollWidth/clientWidth (layout thrashing) on every single event is the actual
    // jank source, not which CSS property the thumb uses.
    let rafId = null;
    let pendingScrollLeft = null;

    const scheduleScrollLeft = (value) => {
      pendingScrollLeft = value;
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          scroller.scrollLeft = pendingScrollLeft;
          updateThumb();
        });
      }
    };

    const cancelScheduledScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // `.slider` has `scroll-snap-type` + `scroll-behavior: smooth` (for arrow-button
    // navigation). Both fight arbitrary intermediate scrollLeft positions during a
    // manual drag — snap tries to settle to the nearest slide, smooth animates toward
    // it — which reads as stutter/lag no matter how the writes are throttled.
    // `transitioning` mirrors the attribute the BBB reference site toggles on
    // slider-component for the same reason; see the [transitioning] override in the
    // section's CSS.
    const beginTransition = () => {
      if (sliderComponent) sliderComponent.setAttribute('transitioning', '');
    };
    const endTransition = () => {
      if (sliderComponent) sliderComponent.removeAttribute('transitioning');
    };

    // --- Drag the custom scrollbar track ---
    let trackDragStartX = 0;
    let trackDragStartScrollLeft = 0;

    const onTrackDragMove = (clientX) => {
      const deltaRatio = (clientX - trackDragStartX) / track.clientWidth;
      scheduleScrollLeft(
        trackDragStartScrollLeft + deltaRatio * (scroller.scrollWidth - scroller.clientWidth),
      );
    };

    const onTrackMouseMove = (event) => onTrackDragMove(event.clientX);
    const onTrackTouchMove = (event) => onTrackDragMove(event.touches[0].clientX);

    const startTrackDrag = (clientX) => {
      track.classList.add('dragging');
      trackDragStartX = clientX;
      trackDragStartScrollLeft = scroller.scrollLeft;
      beginTransition();
    };

    // Move/end listeners are only attached to `document` for the duration of an
    // actual drag (from mousedown/touchstart until release), so simply hovering
    // the scrollbar can never trigger a scroll — there's nothing listening yet.
    const stopTrackDrag = () => {
      track.classList.remove('dragging');
      endTransition();
      cancelScheduledScroll();
      document.removeEventListener('mousemove', onTrackMouseMove);
      document.removeEventListener('mouseup', stopTrackDrag);
      document.removeEventListener('touchmove', onTrackTouchMove);
      document.removeEventListener('touchend', stopTrackDrag);
    };

    track.addEventListener('mousedown', (event) => {
      event.preventDefault();
      startTrackDrag(event.clientX);
      document.addEventListener('mousemove', onTrackMouseMove);
      document.addEventListener('mouseup', stopTrackDrag);
    });

    track.addEventListener(
      'touchstart',
      (event) => {
        startTrackDrag(event.touches[0].clientX);
        document.addEventListener('touchmove', onTrackTouchMove);
        document.addEventListener('touchend', stopTrackDrag);
      },
      { passive: true },
    );

    // --- Drag the slider itself with a mouse ---
    // Touch already scrolls the slider natively, so this only wires up mouse events.
    // The cards contain real links/buttons/inputs, so we can't preventDefault on
    // mousedown (that would block inputs from focusing) — only commit to "this is a
    // drag" once the pointer has actually moved past a small threshold, then swallow
    // the click that would otherwise fire on release.
    let sliderPointerDown = false;
    let sliderHasDragged = false;
    let sliderDragStartX = 0;
    let sliderDragStartScrollLeft = 0;

    const suppressClickOnce = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const onSliderMouseMove = (event) => {
      if (!sliderPointerDown) return;
      const deltaX = event.clientX - sliderDragStartX;

      if (!sliderHasDragged) {
        if (Math.abs(deltaX) < DRAG_CLICK_THRESHOLD_PX) return;
        sliderHasDragged = true;
        scroller.classList.add('slider--dragging');
        beginTransition();
      }

      scheduleScrollLeft(sliderDragStartScrollLeft - deltaX);
    };

    const onSliderMouseUp = () => {
      sliderPointerDown = false;
      scroller.classList.remove('slider--dragging');
      endTransition();
      cancelScheduledScroll();
      document.removeEventListener('mousemove', onSliderMouseMove);
      document.removeEventListener('mouseup', onSliderMouseUp);

      if (sliderHasDragged) {
        // A real drag just happened — swallow the click it would otherwise fire on
        // release (e.g. following a product link), without affecting normal taps.
        document.addEventListener('click', suppressClickOnce, { capture: true, once: true });
      }
    };

    scroller.addEventListener('mousedown', (event) => {
      sliderPointerDown = true;
      sliderHasDragged = false;
      sliderDragStartX = event.clientX;
      sliderDragStartScrollLeft = scroller.scrollLeft;
      document.addEventListener('mousemove', onSliderMouseMove);
      document.addEventListener('mouseup', onSliderMouseUp);
    });

    scroller.addEventListener('scroll', updateThumb, { passive: true });
    window.addEventListener('resize', updateThumb);

    // A single updateThumb() call at script-load time can run before product images
    // have finished loading — scroller.scrollWidth briefly reads as if nothing
    // overflows, so the thumb is computed at 100% and, since nothing else was set
    // up to recalculate it yet, stays wrong until a scroll/resize happens to fire.
    // `load` re-checks once every image has actually settled its layout.
    window.addEventListener('load', updateThumb);
    // Same problem for cards injected after `load` — image load events don't
    // bubble, so catch them on the way down.
    scroller.addEventListener('load', updateThumb, true);

    updateThumb();
  });
};

export default slideshowCustomScrollbar;
